import '../../styles/mandant.css';
import './lohn.css';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { LegalLinks } from '../../components/LegalLinks';
import { LangSwitch, t } from '../../i18n';
import { useConfig } from '../../lib/config';
import { useApi } from '../../lib/useApi';
import { useToast } from '../../lib/toast';
import { QuestionnaireFields, useQuestionnaire } from './Questionnaire';

interface LinkInfo {
  firm: string;
  firmAddress: string;
  kanzlei: string;
  vorname: string;
  nachname: string;
  art: string;
}

// Seite für den Mitarbeiter (ohne Anmeldung): Er öffnet den Link seines Arbeitgebers und füllt seine persönlichen
// Angaben aus. Gehalt und Arbeitszeit hat der Arbeitgeber eingetragen, sie stehen hier nicht. Oben wählt er seine Sprache.
export function AngabenPage() {
  const { token = '' } = useParams();
  const info = useApi<LinkInfo>(`/api/public/angaben/${encodeURIComponent(token)}`);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    document.title = t('Ihre Angaben für die Lohnabrechnung');
  }, []);

  return (
    <div className="mapp">
      <div className="regwrap angaben">
        <div className="in">
          <div className="brandrow">
            <span className="logo wm" role="img" aria-label="Elbcons" />
            <span style={{ marginInlineStart: 'auto' }}>
              <LangSwitch />
            </span>
          </div>
          {sent ? (
            <>
              <h1>{t('Vielen Dank!')}</h1>
              <p className="lead">
                {t('Ihre Angaben sind bei {firm} angekommen. Sie können diese Seite jetzt schließen.', { firm: sent })}
              </p>
            </>
          ) : info.loading ? (
            <div className="spinner" aria-label={t('Lädt')} />
          ) : info.error || !info.data ? (
            <>
              <h1>{t('Link ungültig')}</h1>
              <div className="card gone" role="alert">
                {info.error ?? t('Dieser Link ist ungültig.')}
              </div>
            </>
          ) : (
            <AngabenForm token={token} info={info.data} onSent={setSent} />
          )}
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}

function AngabenForm({ token, info, onSent }: { token: string; info: LinkInfo; onSent(firm: string): void }) {
  const toast = useToast();
  // die Beschäftigungsart steuert Steuerklasse (nicht beim Minijob) und Immatrikulation (Werkstudent)
  const q = useQuestionnaire('mitarbeiter', { vorname: info.vorname, nachname: info.nachname, art: info.art });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = q.check();
    if (problem) {
      toast(problem);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ firm: string }>(`/api/public/angaben/${encodeURIComponent(token)}`, q.formData());
      onSent(res.firm);
      document.querySelector('.regwrap')?.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <>
      <h1>{t('Ihre Angaben für die Lohnabrechnung')}</h1>
      <p className="lead">
        {t(
          '{firm} bittet Sie um Ihre Angaben, damit Sie bei Krankenkasse und Finanzamt angemeldet werden können. Halten Sie bitte Ihren Ausweis, Ihre Steuer-ID und Ihre Sozialversicherungsnummer bereit.',
          { firm: info.firm },
        )}
      </p>
      <Datenschutzhinweise info={info} />
      <form className="form maform" onSubmit={submit} noValidate>
        <QuestionnaireFields q={q} />
        <span className="hint">
          <span className="req">*</span>{' '}
          {t('Pflichtfeld. Ihre Angaben gehen über eine verschlüsselte Verbindung an {firm} und deren Lohnbuchhaltung.', {
            firm: info.firm,
          })}
        </span>
        {error && (
          <p className="formerr" role="alert">
            {error}
          </p>
        )}
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : t('Angaben senden')}
        </button>
      </form>
    </>
  );
}

// Informationen nach Art. 13 DSGVO für den Mitarbeiter: Verantwortlich ist sein Arbeitgeber, die Kanzlei rechnet
// im Auftrag ab. Eingeklappt, damit das Formular oben bleibt.
function Datenschutzhinweise({ info }: { info: LinkInfo }) {
  const { datenschutzUrl } = useConfig();
  const firm = info.firmAddress ? `${info.firm}, ${info.firmAddress}` : info.firm;
  const items = [
    [t('Verantwortlich'), t('{firm} (Ihr Arbeitgeber).', { firm })],
    [
      t('Lohnbuchhaltung'),
      t(
        '{kanzlei} macht die Lohnabrechnung im Auftrag Ihres Arbeitgebers und nutzt Ihre Daten nur dafür (Auftragsverarbeitung nach Art. 28 DSGVO).',
        { kanzlei: info.kanzlei },
      ),
    ],
    [
      t('Zweck'),
      t(
        'Ihre Anmeldung bei Krankenkasse und Finanzamt und Ihre Lohnabrechnung. Rechtsgrundlage sind Ihr Arbeitsvertrag und gesetzliche Pflichten (Art. 6 Abs. 1 b und c DSGVO), für Konfession und Schwerbehinderung Art. 9 Abs. 2 b DSGVO. Die Ausweiskopie nur mit Ihrem Einverständnis (Art. 6 Abs. 1 a DSGVO), das Sie jederzeit widerrufen können.',
      ),
    ],
    [
      t('Pflichtangaben'),
      t('Die meisten Angaben schreibt das Gesetz vor. Fehlt z. B. Ihre Steuer-ID, muss nach Steuerklasse VI abgerechnet werden.'),
    ],
    [
      t('Empfänger'),
      t(
        'Finanzamt, Krankenkasse, Deutsche Rentenversicherung und weitere Stellen, soweit das Gesetz es verlangt (z. B. Berufsgenossenschaft, Agentur für Arbeit, SOKA-BAU). Ihre Daten liegen in Rechenzentren in Deutschland.',
      ),
    ],
    [
      t('Speicherdauer'),
      t(
        'Solange Sie beschäftigt sind, danach bis zum Ende der gesetzlichen Aufbewahrungsfristen (je nach Unterlage 6 bis 10 Jahre). Dann löscht die App sie automatisch.',
      ),
    ],
    [
      t('Ihre Rechte'),
      t(
        'Sie können Auskunft, Berichtigung, Löschung oder Einschränkung verlangen, Ihre Daten mitnehmen und widersprechen (Art. 15 bis 21 DSGVO). Wenden Sie sich dafür an Ihren Arbeitgeber. Außerdem können Sie sich bei einer Datenschutz-Aufsichtsbehörde beschweren (Art. 77 DSGVO).',
      ),
    ],
  ];
  return (
    <details className="tbl dsh">
      <summary>{t('Datenschutzhinweise für Beschäftigte')}</summary>
      <dl>
        {items.map(([label, text]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{text}</dd>
          </div>
        ))}
      </dl>
      {datenschutzUrl && (
        <a href={datenschutzUrl} target="_blank" rel="noopener noreferrer">
          {t('Datenschutzerklärung von {kanzlei}', { kanzlei: info.kanzlei })}
        </a>
      )}
    </details>
  );
}
